"use client";

import { useState } from "react";
import {
  Checkbox,
  DataTable,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useDiagramRef } from "@/components/tools/geometry/diagram";
import {
  type UnitCircleShow,
  UnitCircleSvg,
} from "@/components/tools/geometry/unit-circle-svg";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import {
  type AngleSet,
  allFunctions,
  unitCircleAngle,
  unitCircleAngles,
} from "@/lib/tool-kit/geometry/unit-circle";

/** English strings. A translated content file overrides these through `ui`. */
export const UNIT_CIRCLE_CHART_STRINGS = {
  setLabel: "Angles on the chart",
  sets: {
    standard: "Multiples of 30° and 45°",
    fifteen: "Every 15°",
  },
  showLabel: "Show on the chart",
  showDegrees: "Degrees",
  showRadians: "Radians",
  showCoordinates: "Coordinates (cos θ, sin θ)",
  showTangent: "Tangent",
  showQuadrants: "Quadrant sign notes",
  showTable: "Add a table of values",
  highlightLabel: "Highlight an angle",
  highlightNone: "None",
  print: "Print the chart",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  printNote:
    "The chart prints on one page and the controls stay off the paper. Tick the table option for a second page of values.",
  chartHeading: "Unit Circle",
  chartFormula: "(x, y) = (cos θ, sin θ)",
  chartTitle:
    "The unit circle with the standard angles in degrees and radians and the exact cosine and sine of each",
  angleLabel: "{degrees} degrees",
  zeroLabel: "0° = 360°",
  undefinedText: "undefined",
  quadrantNotes: {
    1: "All positive",
    2: "Sine positive",
    3: "Tangent positive",
    4: "Cosine positive",
  },
  detailsHeading: "Values at the selected angle",
  detailsHint:
    "Select a point on the chart, or choose an angle above, to see all six trig values.",
  angleCard: "Angle",
  quadrantCard: "Quadrant",
  referenceCard: "Reference angle",
  quadrantNames: { 1: "I", 2: "II", 3: "III", 4: "IV" },
  functionHeader: "Function",
  exactHeader: "Exact",
  decimalHeader: "Decimal",
  functionNames: {
    sin: "sin θ",
    cos: "cos θ",
    tan: "tan θ",
    csc: "csc θ",
    sec: "sec θ",
    cot: "cot θ",
  },
  tableHeading: "Exact values",
  degreesHeader: "Degrees",
  radiansHeader: "Radians",
  cosHeader: "cos θ",
  sinHeader: "sin θ",
  tanHeader: "tan θ",
} as const;

type Strings = typeof UNIT_CIRCLE_CHART_STRINGS;

const ID_PREFIX = "uc";
const PRINT_CSS = [
  "@page { margin: 10mm; }",
  "@media print {",
  "  [data-tool-page] > div { max-width: none !important; }",
  "  .uc-sheet { width: auto !important; max-width: 100% !important; max-height: 255mm !important; }",
  "}",
].join("\n");

function Details({
  degrees,
  strings,
  fmt,
}: {
  degrees: number;
  strings: Strings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}) {
  const angle = unitCircleAngle(degrees, strings.undefinedText);
  const rows = allFunctions(degrees, strings.undefinedText).map((row) => [
    strings.functionNames[row.fn],
    row.exact,
    row.value === null
      ? strings.undefinedText
      : fmt(row.value, { maximumFractionDigits: 6 }),
  ]);
  return (
    <div className="space-y-4 print:hidden">
      <StatGrid>
        <StatCard
          emphasis
          label={strings.angleCard}
          value={`${fmt(degrees)}° = ${angle.radians}`}
        />
        <StatCard
          label={strings.quadrantCard}
          value={strings.quadrantNames[angle.quadrant]}
        />
        <StatCard
          label={strings.referenceCard}
          value={`${fmt(angle.reference)}°`}
        />
      </StatGrid>
      <DataTable
        head={[
          strings.functionHeader,
          strings.exactHeader,
          strings.decimalHeader,
        ]}
        rows={rows}
      />
    </div>
  );
}

export default function UnitCircleChartWidget() {
  const strings = useToolUi(UNIT_CIRCLE_CHART_STRINGS);
  const fmt = useToolNumber();
  const svgRef = useDiagramRef();
  const [set, setSet] = useState<AngleSet>("standard");
  const [show, setShow] = useState<UnitCircleShow>({
    degrees: true,
    radians: true,
    coordinates: true,
    tangent: false,
    quadrants: true,
  });
  const [showTable, setShowTable] = useState(false);
  const [selectedRaw, setSelected] = useState<number | null>(null);

  const angles = unitCircleAngles(set, strings.undefinedText);
  const selected = angles.some((angle) => angle.degrees === selectedRaw)
    ? selectedRaw
    : null;

  function toggle(key: keyof UnitCircleShow, value: boolean) {
    setShow({ ...show, [key]: value });
  }

  return (
    <div className="space-y-6">
      <style>{PRINT_CSS}</style>
      <div className="space-y-4 print:hidden">
        <PillGroup
          label={strings.setLabel}
          name={`${ID_PREFIX}-set`}
          onChange={setSet}
          options={(["standard", "fifteen"] as const).map((value) => ({
            value,
            label: strings.sets[value],
          }))}
          value={set}
        />
        <fieldset className="space-y-2">
          <legend className="font-medium text-foreground text-sm">
            {strings.showLabel}
          </legend>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Checkbox
              checked={show.degrees}
              label={strings.showDegrees}
              onChange={(value) => toggle("degrees", value)}
            />
            <Checkbox
              checked={show.radians}
              label={strings.showRadians}
              onChange={(value) => toggle("radians", value)}
            />
            <Checkbox
              checked={show.coordinates}
              label={strings.showCoordinates}
              onChange={(value) => toggle("coordinates", value)}
            />
            <Checkbox
              checked={show.tangent}
              label={strings.showTangent}
              onChange={(value) => toggle("tangent", value)}
            />
            <Checkbox
              checked={show.quadrants}
              label={strings.showQuadrants}
              onChange={(value) => toggle("quadrants", value)}
            />
            <Checkbox
              checked={showTable}
              label={strings.showTable}
              onChange={setShowTable}
            />
          </div>
        </fieldset>
        <div className="max-w-sm">
          <SelectField
            id={`${ID_PREFIX}-highlight`}
            label={strings.highlightLabel}
            onChange={(value) =>
              setSelected(value === "none" ? null : Number(value))
            }
            options={[
              { value: "none", label: strings.highlightNone },
              ...angles.map((angle) => ({
                value: String(angle.degrees),
                label: `${angle.degrees}° (${angle.radians})`,
              })),
            ]}
            value={selected === null ? "none" : String(selected)}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              if (svgRef.current) {
                downloadSvg("unit-circle.svg", svgRef.current);
              }
            }}
            type="button"
          >
            {strings.downloadSvg}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              if (svgRef.current) {
                downloadPng("unit-circle.png", svgRef.current, { scale: 3 });
              }
            }}
            type="button"
          >
            {strings.downloadPng}
          </button>
        </div>
        <p className={HINT}>{strings.printNote}</p>
      </div>
      <UnitCircleSvg
        angleLabel={(degrees) => t(strings.angleLabel, { degrees })}
        angles={angles}
        formula={strings.chartFormula}
        heading={strings.chartHeading}
        onSelect={(degrees) =>
          setSelected(degrees === selected ? null : degrees)
        }
        quadrantNotes={strings.quadrantNotes}
        selected={selected}
        show={show}
        svgRef={svgRef}
        title={strings.chartTitle}
        zeroLabel={strings.zeroLabel}
      />
      <section aria-live="polite" className="space-y-3 print:hidden">
        <h2 className={WIDGET_H2}>{strings.detailsHeading}</h2>
        {selected === null ? (
          <p className="text-muted-foreground text-sm">{strings.detailsHint}</p>
        ) : (
          <Details degrees={selected} fmt={fmt} strings={strings} />
        )}
      </section>
      {showTable ? (
        <section className="break-inside-avoid space-y-3">
          <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
          <DataTable
            head={[
              strings.degreesHeader,
              strings.radiansHeader,
              strings.cosHeader,
              strings.sinHeader,
              strings.tanHeader,
            ]}
            rows={angles.map((angle) => [
              `${angle.degrees}°`,
              angle.radians,
              angle.cos,
              angle.sin,
              angle.tan,
            ])}
          />
        </section>
      ) : null}
    </div>
  );
}
